import React, { useState, useEffect } from "react";
import { Star, ChevronRight, Film, Play, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

// Focusing on Northern India locations
const northIndiaLocations = [
  {
    state: "Jammu & Kashmir",
    city: "Srinagar",
    features: ["Dolby Atmos", "IMAX", "Premium Lounge"],
  },
  {
    state: "Himachal Pradesh",
    city: "Shimla, Una, Mandi, Kullu, Hamirpur",
    features: ["4DX", "Recliner Seats", "Fine Dining"],
  },
  {
    state: "Punjab",
    city: "Chandigarh, Pathankot, Tanda (Dasua), Jalandhar, Barnala, Bathinda",
    features: ["IMAX", "Fine Dining", "VIP Suites"],
  },
  {
    state: "Rajasthan",
    city: "Jhunjhunu",
    features: ["Dolby Vision", "IMAX", "Premium Lounge"],
  },
  {
    state: "Haryana",
    city: "Gurgaon, Faridabad",
    features: ["ScreenX", "Premium Bar", "Private Screenings"],
  },
  {
    state: "Delhi",
    city: "New Delhi",
    features: ["IMAX Laser", "Lounge Access", "Gourmet Dining"],
  },
  {
    state: "Uttar Pradesh",
    city: "Noida",
    features: ["4DX", "Dolby Vision", "Fine Dining"],
  },
  {
    state: "Maharashtra",
    city: "Chembur (Mumbai), Pune",
    features: ["IMAX", "Recliner Seats", "Gourmet Dining"],
  },
];

export default function GrandCinemazHero() {
  const [hoveredState, setHoveredState] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [animate, setAnimate] = useState(false);

  // Animation effect on load
  useEffect(() => {
    setAnimate(true);
  }, []);

  const handleStateClick = (index) => {
    setSelectedState(selectedState === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#3c0d0d] via-[#1b1c3b] to-[#000814] flex items-center justify-center px-6 py-16 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20">
        <div className="absolute top-10 left-10 w-40 h-40 bg-[#b3001b] rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#ff4d4d] rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/4 w-32 h-32 bg-[#ff4d4d] rounded-full blur-3xl animate-pulse"></div>

        {/* Film reel decoration */}
        <div className="absolute -top-20 -right-20 w-64 h-64 border-4 border-[#b3001b]/20 rounded-full"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 border-2 border-[#b3001b]/20 rounded-full"></div>
      </div>

      {/* Content */}
      <div
        className={`text-center max-w-5xl text-white relative z-10 transition-all duration-1000 ${
          animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        {/* Logo and header */}
        <div className="mb-8 flex flex-col items-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#b3001b] to-[#ff4d4d] flex items-center justify-center">
              <Film className="h-6 w-6 text-white" />
            </div>
            <div className="h-10 w-px bg-[#b3001b]/50"></div>
            <div className="text-2xl font-bold tracking-widest text-white">
              GRAND<span className="text-[#ff4d4d]">CINEMAZ</span>
            </div>
          </div>

          <div className="flex items-center justify-center mb-2">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#b3001b]"></div>
            <span className="mx-4 text-[#ff4d4d] font-medium tracking-widest text-sm">
              PREMIER EXPERIENCE
            </span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#b3001b]"></div>
          </div>
        </div>

        <h1 className="text-4xl sm:text-6xl font-extrabold mb-6 leading-tight tracking-wide">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#ff4d4d]">
            NORTHERN INDIA EXPANSION
          </span>
        </h1>

        <p className="text-lg sm:text-xl mb-10 text-gray-300 font-light max-w-2xl mx-auto leading-relaxed">
          Experience cinema reimagined with our ultra-luxury theaters coming to
          premier destinations across Northern India.
        </p>

        <div className="bg-gradient-to-br from-[#1a1a2e]/90 to-[#000814]/95 rounded-2xl shadow-2xl p-8 backdrop-blur-md border border-[#b3001b]/20">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Star className="text-[#b3001b] h-5 w-5 fill-[#b3001b]" />
            <h2 className="text-2xl font-semibold text-white">
              EXCLUSIVE DESTINATIONS
            </h2>
            <Star className="text-[#b3001b] h-5 w-5 fill-[#b3001b]" />
          </div>

          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-6 text-lg mb-6">
            {northIndiaLocations.map((location, index) => (
              <li
                key={index}
                className={`transition-all duration-300 hover:scale-105 group relative cursor-pointer 
                  ${
                    selectedState === index
                      ? "scale-105 bg-gradient-to-r from-[#b3001b]/20 to-[#ff4d4d]/10 rounded-lg"
                      : ""
                  }`}
                onMouseEnter={() => setHoveredState(index)}
                onMouseLeave={() => setHoveredState(null)}
                onClick={() => handleStateClick(index)}
              >
                <div
                  className={`
                  absolute inset-0 bg-gradient-to-r from-[#b3001b]/20 to-[#ff4d4d]/10 
                  rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300
                  ${selectedState === index ? "opacity-100" : ""}
                `}
                ></div>
                <div className="relative p-3 flex items-center">
                  <div
                    className={`w-2 h-2 rounded-full bg-[#b3001b] mr-3 
                    ${
                      hoveredState === index || selectedState === index
                        ? "animate-pulse"
                        : ""
                    }`}
                  ></div>
                  <span
                    className={`font-medium transition-colors duration-300 
                    ${
                      hoveredState === index || selectedState === index
                        ? "text-[#ff4d4d]"
                        : "text-gray-200"
                    }`}
                  >
                    {location.state}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          {/* Interactive details panel */}
          <div
            className={`transition-all duration-500 overflow-hidden ${
              selectedState !== null
                ? "max-h-64 opacity-100"
                : "max-h-0 opacity-0"
            }`}
          >
            {selectedState !== null && (
              <div className="bg-[#0c0c1d]/60 rounded-xl p-6 border border-[#b3001b]/30 mb-6 text-left">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-[#ff4d4d] text-xl font-semibold">
                      {northIndiaLocations[selectedState].city}
                    </h3>
                    <p className="text-gray-300 text-sm">
                      {northIndiaLocations[selectedState].state}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 bg-[#b3001b]/20 px-3 py-1 rounded-full">
                    <Star className="h-4 w-4 text-[#ff4d4d]" />
                    <span className="text-sm text-gray-300">Coming Soon</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {northIndiaLocations[selectedState].features.map(
                    (feature, idx) => (
                      <span
                        key={idx}
                        className="bg-[#b3001b]/10 text-white text-xs px-3 py-1 rounded-full border border-[#b3001b]/20"
                      >
                        {feature}
                      </span>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="pt-6 border-t border-[#b3001b]/20 flex justify-center">
            <Link to="/shows">
              <button className="group flex items-center gap-3 py-2 px-5 bg-gradient-to-r from-[#b3001b] to-[#ff4d4d] rounded-full text-white transition-all duration-300 hover:shadow-lg hover:shadow-[#b3001b]/30">
                <Play className="h-4 w-4 fill-white transition-transform duration-300" />
                <span className="text-sm font-medium tracking-wide">
                  EXPERIENCE THE PREVIEW
                </span>
                <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Link>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-center gap-4">
          <div className="h-px w-16 bg-[#b3001b]/30"></div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#b3001b]" />
            <p className="text-sm text-gray-400 font-light">
              Northern India's Premier Cinema Experience
            </p>
          </div>
          <div className="h-px w-16 bg-[#b3001b]/30"></div>
        </div>
      </div>
    </div>
  );
}
